import React, { createContext, useMemo, useState } from "react";
import { ObjectElaborationSelector } from "../generic/ObjectElaborationSelector";
import { ElaborationStatusItem } from "../../redux/feature/object/remote-elaborations-slice";
import { ObjectPanel } from "./object-panel/ObjectPanel";
import { OBJECT_PANEL_WITH_ELABORATION_NOTIFICATION } from "../../utils/constants/notification";
import { CoObjectItem } from "../interfaces/CoObject";
import { ElaborationWrapper } from "./ElaborationWrapper";
import { DocumentPartMetadata } from "../../api/models/document-part-metadata";
import { DocumentPart } from "../../api/models";

export interface ObjectPanelElaborationWrapperProps {
  isEdit: boolean;
  coObject: CoObjectItem;
  documentPart: DocumentPart;
  documentPartMetadata: DocumentPartMetadata;
}

export interface CoObjectContextWrapper {
  coObject: CoObjectItem;
  update: (coObject: CoObjectItem) => void;
}

export interface DocumentPartContextWrapper {
  documentPart: DocumentPart;
  update: (documentPart: DocumentPart) => void;
}

export const CoObjectContext = createContext<CoObjectContextWrapper | null>(null);
export const DocumentPartContext = createContext<DocumentPartContextWrapper | null>(null);

export const ObjectPanelElaborationWrapper: React.FunctionComponent<ObjectPanelElaborationWrapperProps> = (props) => {
  const [coObject, setCoObject] = useState<CoObjectItem>(props.coObject);
  const [documentPart, setDocumentPart] = useState<DocumentPart>(props.documentPart);
  const [documentPartMetadata] = useState<DocumentPartMetadata>(props.documentPartMetadata);
  const documentPartContextValue = useMemo(
    () => ({ documentPart, update: (newDocumentPart: DocumentPart) => setDocumentPart(newDocumentPart) }),
    [documentPart]
  );
  const coObjectContextValue = useMemo(
    () => ({ coObject, update: (newCoObject: CoObjectItem) => setCoObject(newCoObject) }),
    [coObject]
  );
  return (
    <DocumentPartContext.Provider value={documentPartContextValue}>
      <CoObjectContext.Provider value={coObjectContextValue}>
        <ObjectElaborationSelector coObject={coObject}>
          {(_, elaborationItem) => (
            <ObjectPanelWithElaboration
              coObject={coObject}
              isEdit={props.isEdit}
              documentPartMetadata={documentPartMetadata}
              elaborationItem={elaborationItem}
            />
          )}
        </ObjectElaborationSelector>
      </CoObjectContext.Provider>
    </DocumentPartContext.Provider>
  );
};

interface ObjectPanelWithElaborationProps {
  isEdit: boolean;
  coObject: CoObjectItem;
  documentPartMetadata: DocumentPartMetadata;
  elaborationItem?: ElaborationStatusItem;
}

export const ObjectPanelWithElaboration: React.FunctionComponent<ObjectPanelWithElaborationProps> = (props) => {
  const { elaborationItem, documentPartMetadata, coObject, isEdit } = props;
  const notificationKey = `${OBJECT_PANEL_WITH_ELABORATION_NOTIFICATION}-${coObject.contentControlId}`;

  return (
    <ElaborationWrapper elaborationItem={elaborationItem} notificationKey={notificationKey}>
      <ObjectPanel isEdit={isEdit} coObject={coObject} documentPartMetadata={documentPartMetadata} />
    </ElaborationWrapper>
  );
};
